/* eslint-disable react-refresh/only-export-components */
import cn from 'classnames'
import omit from 'lodash/omit'
import uniqueId from 'lodash/uniqueId'
import { ReactElement } from 'react'
import './styles.css'

export enum PLACEHOLDER_TYPE {
  IMAGE = 'image',
  HEADING = 'heading',
  TEXT = 'text',
  BODY_TEXT = 'bodyText',
  PARAGRAPH = 'paragraph',
  CHAT_CONVERSATION = 'chatConversation',
}

export enum SHADE {
  LIGHT = 'light',
  DARK = 'dark',
}

export interface PlaceholderProps {
  className?: string
  inline?: boolean
  lines?: number[]
  loaderClassName?: string
  containerClass?: string
  shade?: SHADE
  type?: PLACEHOLDER_TYPE
}
interface BaseProps {
  className?: string
  inline?: boolean
  loaderClassName?: string
  containerClass?: string
  shade: SHADE
  type: PLACEHOLDER_TYPE
  style?: {
    [key: string]: string | number
  }
}

const PlaceholderBase = (baseProps: BaseProps) => {
  const extraProps = omit(baseProps, [
    'className',
    'inline',
    'loaderClassName',
    'shade',
    'type',
  ])

  const { className, inline, loaderClassName, shade, type } = baseProps

  return (
    <div
      className={cn(
        className,
        'placeholder',
        {
          'placeholder--light': shade === SHADE.LIGHT,
          'placeholder--dark': shade === SHADE.DARK,
          'placeholder--heading': type === PLACEHOLDER_TYPE.HEADING,
          'placeholder--text': type === PLACEHOLDER_TYPE.TEXT,
          'placeholder--bodyText': type === PLACEHOLDER_TYPE.BODY_TEXT,
          'placeholder--image': type === PLACEHOLDER_TYPE.IMAGE,
          'placeholder--paragraph': type === PLACEHOLDER_TYPE.PARAGRAPH,
          inline,
        },
        // Can also be styled by providing custom className
        loaderClassName,
      )}
      {...extraProps}
    />
  )
}

const Placeholder = ({
  className,
  inline = false,
  lines = [8.2, 9, 4, 0, 4.5, 5],
  loaderClassName,
  containerClass,
  shade = SHADE.DARK,
  type = PLACEHOLDER_TYPE.BODY_TEXT,
}: Props) => {
  const renderParagraph = (lines: number[], props: BaseProps) => {
    const placeholderLines: ReactElement[] = []
    for (let line = 0; line <= lines.length; line += 1) {
      const width = `${lines[line] * 10}%`
      const style = { width }
      placeholderLines.push(
        <PlaceholderBase {...props} key={line} style={style} />,
      )
    }
    return placeholderLines
  }

  const renderChatConversation = (props: BaseProps) => {
    return Array.from({ length: 4 }).map(() => (
      <div key={uniqueId('conversationPlaceholder')} className="flex mb-8">
        <div className="flex-shrink-0">
          <span className="w-10 h-10 block bg-gray-100 rounded-full"></span>
        </div>
        <div className="flex flex-col gap-2 w-full ml-4">
          <div className="flex gap-4 w-full">
            <PlaceholderBase {...props} type={PLACEHOLDER_TYPE.BODY_TEXT} />
            <div className="w-24">
              <PlaceholderBase {...props} type={PLACEHOLDER_TYPE.BODY_TEXT} />
            </div>
          </div>
          <PlaceholderBase {...props} type={PLACEHOLDER_TYPE.BODY_TEXT} />
        </div>
      </div>
    ))
  }

  const renderPlaceholder = (placeholderType: PLACEHOLDER_TYPE) => {
    const baseProps = {
      className,
      inline,
      loaderClassName,
      shade,
      type: placeholderType,
    }

    if (placeholderType === PLACEHOLDER_TYPE.PARAGRAPH) {
      return renderParagraph(lines, baseProps)
    }

    if (placeholderType === PLACEHOLDER_TYPE.CHAT_CONVERSATION) {
      return renderChatConversation(baseProps)
    }

    return (
      <PlaceholderBase
        {...baseProps}
        type={placeholderType || PLACEHOLDER_TYPE.BODY_TEXT}
      />
    )
  }

  return (
    <div
      className={cn('animate-pulse', containerClass)}
      data-testid="placeholder-container"
    >
      {renderPlaceholder(type)}
    </div>
  )
}

export default Placeholder
